'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { NextIntlClientProvider, useLocale as useNextIntlLocale, useTranslations } from 'next-intl'
import type { AbstractIntlMessages } from 'next-intl'
import { APP_TIME_ZONE, getStoredLocale, setStoredLocale, type Locale } from '@/lib/i18n'
import enGB from '@/messages/en-GB.json'

const DEFAULT_LOCALE: Locale = 'en-GB'
const DEFAULT_MESSAGES = enGB as AbstractIntlMessages

async function loadMessages(locale: Locale): Promise<AbstractIntlMessages> {
  if (locale === 'en-GB') return DEFAULT_MESSAGES
  if (locale === 'en-US') {
    return (await import('@/messages/en-US.json')).default as AbstractIntlMessages
  }
  return (await import('@/messages/nl.json')).default as AbstractIntlMessages
}

type SetLocaleContextValue = (locale: Locale) => void
const SetLocaleContext = createContext<SetLocaleContextValue | null>(null)

function LocaleInner({ children }: { children: ReactNode }) {
  const locale = useNextIntlLocale() as Locale
  const setLocale = useContext(SetLocaleContext)!
  const t = useTranslations()
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale])
  return (
    <LocaleConsumerContext.Provider value={value}>
      {children}
    </LocaleConsumerContext.Provider>
  )
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE)
  const [messages, setMessages] = useState<AbstractIntlMessages>(DEFAULT_MESSAGES)

  useEffect(() => {
    let cancelled = false
    const stored = getStoredLocale()
    if (!stored || stored === DEFAULT_LOCALE) return
    void loadMessages(stored).then((m) => {
      if (!cancelled) {
        setMessages(m)
        setLocaleState(stored)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const lang = locale === 'nl' ? 'nl' : locale === 'en-US' ? 'en-US' : 'en-GB'
    document.documentElement.lang = lang
  }, [locale])

  const setLocale = useCallback((newLocale: Locale) => {
    setStoredLocale(newLocale)
    if (newLocale === 'en-GB') {
      setMessages(DEFAULT_MESSAGES)
      setLocaleState(newLocale)
      return
    }
    void loadMessages(newLocale).then((m) => {
      setMessages(m)
      setLocaleState(newLocale)
    })
  }, [])

  return (
    <SetLocaleContext.Provider value={setLocale}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone={APP_TIME_ZONE}>
        <LocaleInner>{children}</LocaleInner>
      </NextIntlClientProvider>
    </SetLocaleContext.Provider>
  )
}

const LocaleConsumerContext = createContext<{
  locale: Locale
  setLocale: SetLocaleContextValue
  t: ReturnType<typeof useTranslations>
} | null>(null)

export function useLocale() {
  const ctx = useContext(LocaleConsumerContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}
